// src/pages/Community/News.js
import React, { useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import useScrollAnimation from '../../hooks/useScrollAnimation';
import { AnimatedSection } from '../../components/common/AnimatedSection';

const PageContainer = styled.div`
  min-height: 100vh;
  background: #ffffff;
  padding-top: 10%;
  padding-bottom: 3rem;
  @media (max-width: 768px) {
    padding-top: 30%;
  }
`;

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 3rem;

  @media (max-width: 768px) {
    padding: 0 1.5rem;
  }
`;

const PageHeader = styled.div`
  text-align: center;
  margin-bottom: 3rem;
  padding: 3rem 0;
  @media (max-width: 768px) {
    margin-bottom: 2rem;
  }
  
`;

const PageTitle = styled.h1`
  font-size: 2rem;
  font-weight: 700;
  color: #111111;
  margin-bottom: 1.5rem;
  letter-spacing: -1px;

  @media (max-width: 768px) {
    font-size: 2rem;
  }
`;

const PageSubtitle = styled.p`
  font-size: 1.1rem;
  color: #6b7280;
  font-weight: 400;
  @media (max-width: 768px) {
    font-size: 0.9rem;
  }
`;

const BoardContainer = styled.div`
  background: #ffffff;
  border-top: 1px solid #e5e7eb;
  border-bottom: 1px solid #e5e7eb;
  border-radius: 0;
  overflow: hidden;
  margin-bottom: 2rem;
`;

const BoardHeader = styled.div`
  background: #f8fafc;
  border-bottom: 1px solid #e5e7eb;
  padding: 1rem 2rem;
  display: grid;
  grid-template-columns: 1fr 150px;
  font-weight: 600;
  color: #374151;
  font-size: 1rem;

  @media (max-width: 768px) {
    padding: 1rem 1rem;
    grid-template-columns: 1fr 100px;
  }
`;

const HeaderCell = styled.div`
  text-align: center;
`;

const BoardBody = styled.div`
  background: #ffffff;
`;

const BoardRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 150px;
  padding: 1.2rem 2rem;
  border-bottom: 1px solid #f1f5f9;
  transition: background-color 0.2s ease;
  cursor: default;

  &:hover {
    background: #f8fafc;
  }

  &:last-child {
    border-bottom: none;
  }

  @media (max-width: 768px) {
    padding: 1rem 1rem;
    grid-template-columns: 1fr 100px;
  }
`;

const RowCell = styled.div`
  display: flex;
  align-items: center;
  font-size: 1.1rem;

  &.title {
    justify-content: flex-start;
    color: #111111;
    font-weight: 500;
    line-height: 1.5;

    &:hover {
      color: #3b82f6;
    }
  }

  &.date {
    justify-content: center;
    color: #6b7280;
    font-size: 1rem;
  }

  @media (max-width: 768px) {
    &.title {
      font-size: 0.85rem;
    }
    &.date {
      font-size: 0.8rem;
    }
  }
`;

const Pagination = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 0.5rem;
  margin: 2rem 0;
`;

const PageButton = styled.button`
  padding: 0.5rem 0.75rem;
  border: 1px solid #e5e7eb;
  background: ${props => props.active ? '#3b82f6' : '#ffffff'};
  color: ${props => props.active ? '#ffffff' : '#6b7280'};
  border-radius: 6px;
  font-size: 0.9rem;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    border-color: #3b82f6;
    color: ${props => props.active ? '#ffffff' : '#3b82f6'};
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const SearchSection = styled.div`
  display: flex;
  justify-content: center;
  gap: 1rem;
  margin: 2rem 0;

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: center;
  }
`;

const SearchSelect = styled.select`
  padding: 0.5rem;
  border: 1px solid #e5e7eb;
  border-radius: 6px;
  font-size: 0.9rem;
  color: #6b7280;
`;

const SearchInput = styled.input`
  padding: 0.5rem 1rem;
  border: 1px solid #e5e7eb;
  border-radius: 6px;
  font-size: 0.9rem;
  width: 300px;

  @media (max-width: 768px) {
    width: 100%;
    max-width: 300px;
  }
`;

const SearchButton = styled.button`
  padding: 0.5rem 1.5rem;
  background: #3b82f6;
  color: #ffffff;
  border: none;
  border-radius: 6px;
  font-size: 0.9rem;
  cursor: pointer;
  transition: background-color 0.2s ease;

  &:hover {
    background: #2563eb;
  }
`;

const TitleLink = styled.a`
  display: inline-block;
  color: #111111;
  font-weight: 500;
  line-height: 1.5;
  text-decoration: none;

  &:hover {
    color: #3b82f6;
    text-decoration: underline;
  }
`;

/** 폴백용 기본 데이터 (백엔드가 비었거나 실패 시 사용) */
const fallbackNews = [
  { id: 1,  title: "광주광역시, 2025년 명품강소기업 32개사 선정 '맞춤형 지원'", date: "2025.08.27", url: "https://www.dailysportshankook.co.kr/news/articleView.html?idxno=412956" },
  { id: 2,  title: "2025년 제2차 혁신제품에 광주·전남 8개사 제품 지정", date: "2025.07.23", url: "https://www.yna.co.kr/view/AKR20250723127900054" },
  { id: 3,  title: "“실증은 광주에서…실리콘밸리 부럽지 않다”", date: "2025.03.11", url: "https://www.etnews.com/20250311000396" },
  { id: 4,  title: "[포토닉스 코리아 2023]세연이앤에스, 캐리어형 비상표시장치·스마트 안전경고시스템 등 출품", date: "2023.11.15", url: "https://www.etnews.com/20231106000339" },
  { id: 5,  title: "광주, 2023년 예비-명품강소기업 13곳 선정", date: "2023.09.22", url: "https://www.etnews.com/20250311000396" },
  { id: 6,  title: "불꽃 감지 기술로 화재예방 등 재난 안전제품 22개 신규 인증", date: "2022.07.21", url: "https://www.anjunj.com/news/articleView.html?idxno=33934" },
  { id: 7,  title: "세연이앤에스, 2021 국제광융합산업전시회 및 컨퍼런스 참가... 도로 위의 안전 지킴이 비상표시장치 선보여!", date: "2021.11.26", url: "https://kr.aving.net/news/articleView.html?idxno=1658266" },
  { id: 8,  title: "㈜세연이엔에스, 광주시 동구에 교통안전시설물 기부", date: "2021.11.24", url: "https://www.dnews.co.kr/uhtml/view.jsp?idxno=202111241156420510330" },
  { id: 9,  title: "광주광역시, ‘전국에서 가장 안전한 광주 만들기’ 제2호 안전마을, 안전학교, 안전기업 인증·협약식 개최", date: "2021.03.29", url: "https://www.igj.co.kr/news/articleView.html?idxno=197943" },
  { id: 10, title: "세연이앤에스, 국무총리표창", date: "2018.05.16", url: "https://www.yna.co.kr/view/PYH20180516115800013" },
];

function News() {
  const API = process.env.REACT_APP_API_BASE || 'http://localhost:8080';

  const [headerRef, headerVisible] = useScrollAnimation();
  const [boardRef, boardVisible] = useScrollAnimation();

  const [items, setItems] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [err, setErr] = useState('');

  // 검색/페이지네이션 상태
  const [currentPage, setCurrentPage] = useState(1);
  const [searchType, setSearchType] = useState('title');
  const [searchTerm, setSearchTerm] = useState('');

  const itemsPerPage = 10;

  // 목록 로드 (백엔드 -> 실패/빈 배열이면 폴백)
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const r = await fetch(`${API}/api/news`, { credentials: 'include' });
        if (!r.ok) throw new Error(await r.text());
        const j = await r.json();
        const arr = Array.isArray(j) ? j : [];
        if (alive) {
          setItems(arr.length ? arr : fallbackNews);
          setErr('');
        }
      } catch (e) {
        if (alive) {
          setItems(fallbackNews);
          setErr('서버에서 뉴스를 불러오지 못해 임시 목록을 표시합니다.');
        }
      } finally {
        if (alive) setLoaded(true);
      }
    })();
    return () => { alive = false; };
  }, [API]);

  // 검색 결과 메모
  const filtered = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    if (!term) return items;
    if (searchType === 'title') {
      return items.filter(it => (it.title || '').toLowerCase().includes(term));
    }
    // 'content' 선택 시: 실제 content 필드는 없으므로 title + url에서 검색
    return items.filter(it =>
        ((it.title || '') + ' ' + (it.url || '')).toLowerCase().includes(term)
    );
  }, [items, searchType, searchTerm]);

  const totalPages = Math.ceil(filtered.length / itemsPerPage) || 1;

  const getCurrentPageData = () => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    return filtered.slice(startIndex, endIndex);
  };

  const handleSearch = () => {
    setCurrentPage(1);
  };

  return (
      <PageContainer>
        <Container>
          <AnimatedSection
              ref={headerRef}
              isVisible={headerVisible}
              animation="fadeInUp"
          >
            <PageHeader>
              <PageTitle>뉴스</PageTitle>
              <PageSubtitle>
                세연이앤에스의 최신 소식과 성과를 확인하세요
              </PageSubtitle>
            </PageHeader>
          </AnimatedSection>

          <AnimatedSection
              ref={boardRef}
              isVisible={boardVisible}
              animation="fadeInUp"
              delay="0.2s"
          >
            {err && (
                <div style={{ color: '#b45309', marginBottom: '8px' }}>
                  {err}
                </div>
            )}

            <BoardContainer>
              <BoardHeader>
                <HeaderCell>제목</HeaderCell>
                <HeaderCell>작성일</HeaderCell>
              </BoardHeader>

              <BoardBody>
                {!loaded && (
                    <div style={{ padding: '24px', color: '#6b7280', textAlign: 'center' }}>
                      불러오는 중…
                    </div>
                )}

                {loaded && getCurrentPageData().map((item) => (
                    <BoardRow key={item.id ?? `${item.title}-${item.date}`}>
                      <RowCell className="title">
                        {item.url ? (
                            <TitleLink
                                href={item.url}
                                target="_blank"
                                rel="noopener noreferrer"
                                aria-label={`${item.title} 기사로 이동`}
                            >
                              {item.title}
                            </TitleLink>
                        ) : (
                            item.title
                        )}
                      </RowCell>
                      <RowCell className="date">{item.date || '-'}</RowCell>
                    </BoardRow>
                ))}

                {loaded && filtered.length === 0 && (
                    <div style={{ padding: '24px', color: '#6b7280', textAlign: 'center' }}>
                      검색 결과가 없습니다.
                    </div>
                )}
              </BoardBody>
            </BoardContainer>

            <Pagination>
              <PageButton
                  onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                  disabled={currentPage === 1}
              >
                이전
              </PageButton>

              {[...Array(totalPages)].map((_, index) => (
                  <PageButton
                      key={index + 1}
                      active={currentPage === index + 1}
                      onClick={() => setCurrentPage(index + 1)}
                  >
                    {index + 1}
                  </PageButton>
              ))}

              <PageButton
                  onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                  disabled={currentPage === totalPages}
              >
                다음
              </PageButton>
            </Pagination>

            <SearchSection>
              <SearchSelect
                  value={searchType}
                  onChange={(e) => setSearchType(e.target.value)}
              >
                <option value="title">제목</option>
                <option value="content">내용</option>
              </SearchSelect>

              <SearchInput
                  type="text"
                  placeholder="검색어를 입력하세요"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
              />

              <SearchButton onClick={handleSearch}>
                검색
              </SearchButton>
            </SearchSection>
          </AnimatedSection>
        </Container>
      </PageContainer>
  );
}

export default News;
